"use client";

import { useParams, useNavigate } from "react-router-dom";
import {
  useGetUserByIdQuery,
  useEditUserByIdMutation,
  useDeleteUserByIdMutation,
} from "@/features/api/authApi";

import {
  useGetOrdersQuery,
} from "@/features/api/orderApi";

import {
  useGetCoursesQuery,
} from "@/features/api/courseApi";

import { useState, useEffect, useMemo } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Loader2,
  Trash,
  Eye,
  EyeOff,
  Edit,
} from "lucide-react";

import { toast } from "sonner";

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  /* ================= USER ================= */
  const { data, isLoading: isFetching } = useGetUserByIdQuery(id, {
    skip: !id,
  });

  const [editUser, { isLoading: isUpdating }] =
    useEditUserByIdMutation();

  const [deleteUser] = useDeleteUserByIdMutation();

  /* ================= COURSES ================= */
  const { data: coursesData } = useGetCoursesQuery();
  const allCourses = coursesData?.courses || [];

  /* ================= ORDERS ================= */
  const { data: ordersData, isLoading: ordersLoading } =
    useGetOrdersQuery({ limit: 100 }, { skip: !id });

  /* ================= FORM ================= */
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "user",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (data?.user) {
      const u = data.user;
      setForm({
        name: u.name || "",
        email: u.email || "",
        phone: u.tel || "",
        role: u.role || "user",
        password: "",
      });
    }
  }, [data]);

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  /* ================= PURCHASED COURSES ================= */
  const purchasedCourses = useMemo(() => {
    if (!ordersData?.data?.length) return [];

    const ids = ordersData.data
      .filter((o) => o.status === "SUCCESS" && o.userId === id)
      .map((o) => o.courseId);

    return allCourses.filter((c) => ids.includes(c._id));
  }, [ordersData, allCourses, id]);

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;

      await editUser({ id, data: payload }).unwrap();

      toast.success("User updated");
      navigate("/admin/users");
    } catch (err) {
      toast.error("Update failed");
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async () => {
    if (form.role === "admin") {
      return toast.error("Cannot delete admin");
    }

    if (!window.confirm("Delete user?")) return;

    await deleteUser(id);
    toast.success("Deleted");
    navigate("/admin/users");
  };

  if (isFetching) {
    return (
      <div className="flex justify-center mt-10">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  /* ================= UI ================= */
  return (
    <div className="max-w-5xl mx-auto space-y-8 mt-10">

      {/* ================= EDIT FORM ================= */}
      <Card className="relative">
        {form.role !== "admin" && (
          <Button
            size="icon"
            variant="destructive"
            className="absolute top-4 right-4"
            onClick={handleDelete}
          >
            <Trash size={18} />
          </Button>
        )}

        <CardHeader>
          <CardTitle>Edit User</CardTitle>
          <CardDescription>
            Update user details
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">

            <InputField label="Name" name="name" value={form.name} onChange={handleChange} />
            <InputField label="Email" name="email" value={form.email} onChange={handleChange} />
            <InputField label="Phone" name="phone" value={form.phone} onChange={handleChange} />

            {/* ROLE */}
            <div>
              <Label>Role</Label>
              <select
                name="role"
                value={form.role}
                onChange={handleChange}
                className="w-full border p-2 rounded"
              >
                <option value="user">User</option>
                <option value="student">Student</option>
                <option value="instructor">Instructor</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* PASSWORD */}
            <div className="relative">
              <Label>Password</Label>
              <Input
                name="password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={handleChange}
                placeholder="Leave blank"
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute right-3 top-8"
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => navigate("/admin/users")}
              >
                Back
              </Button>

              <Button type="submit">
                {isUpdating ? <Loader2 className="animate-spin" /> : "Save"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* ================= COURSES TABLE ================= */}
      <Card>
        <CardHeader>
          <CardTitle>Purchased Courses</CardTitle>
          <CardDescription>
            Courses enrolled by this student
          </CardDescription>
        </CardHeader>

        <CardContent>
          {ordersLoading ? (
            <Loader2 className="animate-spin" />
          ) : purchasedCourses.length === 0 ? (
            <p>No courses purchased</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Course</TableHead>
                  <TableHead>Instructor</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {purchasedCourses.map((course) => (
                  <TableRow key={course._id}>
                    <TableCell className="font-medium">
                      {course.title}
                    </TableCell>

                    <TableCell>
                      {course.instructorName || "N/A"}
                    </TableCell>

                    <TableCell>
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/admin/courses/${course.slug}/result/${id}`
                          )
                        }
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        View Result
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* ================= REUSABLE INPUT ================= */
const InputField = ({ label, name, value, onChange }) => (
  <div>
    <Label>{label}</Label>
    <Input name={name} value={value} onChange={onChange} />
  </div>
);

export default EditUser;